"use client";

import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@shared/src/utils/cn";

const Tabs = TabsPrimitive.Root;

const tabsListVariants = cva("inline-flex items-center justify-center transition-all", {
  variants: {
    variant: {
      default: "h-9 rounded-lg bg-muted p-1 text-muted-foreground",
      "button-contained": "bg-transparent p-1.5 border border-border-tertiary rounded-xl gap-0",
      "button-full": "w-full gap-2 p-6 bg-transparent border-b border-border-tertiary",
      underline: "flex w-full gap-1 border-b border-border-tertiary justify-start",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const tabsTriggerVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "rounded-md px-3 py-1 text-sm font-medium ring-offset-background data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow",
        "button-contained":
          "px-6 py-2 rounded-2xl duration-200 !text-small data-[state=active]:bg-surfaces-bg-secondary data-[state=active]:text-text-primary data-[state=inactive]:text-text-tertiary",
        "button-full":
          "flex-1 px-6 py-2 rounded-2xl duration-200 !text-small data-[state=active]:bg-surfaces-bg-secondary data-[state=active]:text-text-primary data-[state=inactive]:text-text-tertiary",
        underline:
          "px-6 py-4 text-text-tertiary font-medium duration-200 !text-small relative data-[state=active]:text-text-primary data-[state=inactive]:text-text-tertiary hover:text-text-primary data-[state=active]:after:absolute data-[state=active]:after:bottom-0 data-[state=active]:after:left-0 data-[state=active]:after:right-0 data-[state=active]:after:h-0.5 data-[state=active]:after:bg-text-primary",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface TabsListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    VariantProps<typeof tabsListVariants> {
  activeColor?: string;
}

export interface TabsTriggerProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
    VariantProps<typeof tabsTriggerVariants> {
  activeColor?: string;
}

const TabsList = React.forwardRef<React.ElementRef<typeof TabsPrimitive.List>, TabsListProps>(
  ({ className, variant, activeColor, ...props }, ref) => (
    <TabsPrimitive.List ref={ref} className={cn(tabsListVariants({ variant }), className)} {...props} />
  ),
);
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<React.ElementRef<typeof TabsPrimitive.Trigger>, TabsTriggerProps>(
  ({ className, variant, activeColor, ...props }, ref) => {
    const getActiveStyle = () => {
      if (!activeColor) return undefined;

      // If it's already a CSS variable or direct color, use it directly
      if (activeColor.startsWith("var(") || activeColor.startsWith("#") || activeColor.startsWith("rgb")) {
        if (variant === "underline") {
          return {
            "--active-color": activeColor,
          } as React.CSSProperties;
        } else if (variant === "button-contained" || variant === "button-full") {
          return {
            "--active-bg-color": activeColor,
          } as React.CSSProperties;
        }
      }

      // Handle custom Tailwind classes by converting them to CSS variables
      if (activeColor.startsWith("bg-surfaces-bg-")) {
        const colorVar = `var(--${activeColor.replace("bg-", "").replace(/-/g, "-")})`;
        if (variant === "underline") {
          return {
            "--active-color": colorVar,
          } as React.CSSProperties;
        } else if (variant === "button-contained" || variant === "button-full") {
          return {
            "--active-bg-color": colorVar,
          } as React.CSSProperties;
        }
      }

      return undefined;
    };

    const getActiveClasses = () => {
      if (!activeColor) return "";

      // If it's a CSS variable or direct color, use CSS custom properties
      if (
        activeColor.startsWith("var(") ||
        activeColor.startsWith("#") ||
        activeColor.startsWith("rgb") ||
        activeColor.startsWith("bg-surfaces-bg-")
      ) {
        if (variant === "underline") {
          return "data-[state=active]:after:bg-[var(--active-color)]";
        } else if (variant === "button-contained" || variant === "button-full") {
          return "data-[state=active]:bg-[var(--active-bg-color)]";
        }
      } else {
        // For standard Tailwind classes, use them directly
        if (variant === "underline") {
          return `data-[state=active]:after:${activeColor}`;
        } else if (variant === "button-contained" || variant === "button-full") {
          return `data-[state=active]:${activeColor}`;
        }
      }

      return "";
    };

    return (
      <TabsPrimitive.Trigger
        ref={ref}
        className={cn(tabsTriggerVariants({ variant }), getActiveClasses(), className)}
        style={getActiveStyle()}
        {...props}
      />
    );
  },
);
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-6 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
